import { useEffect, useState } from "react";
import axiosInstance from "./../api";
import { Column } from "@ant-design/plots";
import dayjs from "dayjs";
import {
    Button,
    Card,
    Col,
    Collapse,
    DatePicker,
    Divider,
    Form,
    Row,
    Select,
    Space,
    Spin,
} from "antd";
import { useSearchParams } from "react-router-dom";
import { useForm } from "antd/lib/form/Form";
import PeriodRangePicker from "./PickerWithType";
import { filterOption } from "../util";
import BackTitle from "../common/BackTitle";

const selectOptions = [
    {
        value: "CUSTOMER",
        label: "Cliente",
    },
    {
        value: "USER",
        label: "Utente",
    },
    {
        value: "TEAM",
        label: "Squadra",
    },
];

const TimeChart = ({ data, period, startDate, endDate }) => {
    startDate = startDate.startOf(period);
    endDate = endDate.endOf(period);
    const diff = endDate.diff(startDate, period);

    const dateFormatter = {
        QUARTER: (date) => `Q${date.quarter()} ${date.year()}`,
        MONTH: (date) => date.format("MM-YYYY"),
        YEAR: (date) => date.format("YYYY"),
    }[period];

    const toAdd = [...Array(Math.ceil(diff + 1)).keys()]
        .map((i) => startDate.add(i, period).format("YYYY-MM-DD"))
        .filter((x) => !data.some((y) => y.date === x))
        .map((x) => ({ x, y: 0, series: data[0]?.series, group: data[0]?.group }));

    data = [...data, ...toAdd].sort((a, b) => a.x.localeCompare(b.x));

    const isStack = !!data[0]?.series || !!data[0]?.group;
    const config = {
        data,
        animation: false,
        xField: "x",
        yField: "y",
        isStack,
        seriesField: isStack ? "series" : false,
        legend: {
            position: "top",
        },
        autoResize: true,
        xAxis: {
            label: {
                autoHide: true,
                autoRotate: false,
            },
        },
        meta: {
            y: {
                alias: "Totale",
                formatter: (x) =>
                    x.toLocaleString("it-IT", { minimumFractionDigits: 2, useGrouping: true }),
            },
            x: {
                alias: "Periodo",
                formatter: (x) => dateFormatter(dayjs(x)),
            },
        },
        connectedArea: {
            style: (oldStyle, element) => {
                return {
                    fill: "rgba(0,0,0,0.25)",
                    stroke: oldStyle.fill,
                    lineWidth: 0.5,
                };
            },
        },
    };
    return <Column {...config} />;
};

export default function KpiDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [form] = useForm();
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [kpis, setKpis] = useState([]);
    const [collapseKey, setCollapseKey] = useState(1);
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const initialValues = {
        interval: {
            period: "MONTH",
            interval: [dayjs().startOf("month").subtract(12, "month"), dayjs().endOf("month")],
        },
    };

    useEffect(() => {
        axiosInstance
            .get("customer/compact")
            .then(({ data }) => setCustomers(data.map((x) => ({ label: x.name, value: x.id }))));

        axiosInstance
            .get("kpi")
            .then(({ data }) => setKpis(data.map((x) => ({ label: x.description, value: x.id }))));

        axiosInstance
            .get("team")
            .then(({ data }) => setTeams(data.map((x) => ({ label: x.name, value: x.id }))));

        axiosInstance
            .get("user")
            .then(({ data }) =>
                setUsers(data.map((x) => ({ label: x.firstName + " " + x.lastName, value: x.id }))),
            );

        if (!searchParams.get("startDate")) {
            searchParams.set("startDate", initialValues.interval.interval[0].format("YYYY-MM-DD"));
            searchParams.set("endDate", initialValues.interval.interval[1].format("YYYY-MM-DD"));
            searchParams.set("period", initialValues.interval.period);
            setSearchParams(searchParams, { replace: true });
        }
    }, []);

    useEffect(() => {
        if (!!searchParams.get("startDate")) {
            setLoading(true);
            const formValues = {
                interval: {
                    period: searchParams.get("period"),
                    interval: [
                        dayjs(searchParams.get("startDate")),
                        dayjs(searchParams.get("endDate")),
                    ],
                },
                kpi: searchParams.get("kpi")?.split(","),
                customerId:
                    searchParams.get("customerId") && parseInt(searchParams.get("customerId")),
                teamId: searchParams.get("teamId") && parseInt(searchParams.get("teamId")),
                userId: searchParams.get("userId") && parseInt(searchParams.get("userId")),
                group: searchParams.get("group"),
            };
            form.setFieldsValue(formValues);

            axiosInstance
                .get("kpi/timeseries", {
                    params: [...searchParams.entries()].reduce(
                        (acc, [k, v]) => ({ ...acc, [k]: v }),
                        {},
                    ),
                })
                .then(({ data }) => {
                    const mapped = data.reduce(
                        (acc, curr) => ({
                            ...acc,
                            [curr.description]: [...(acc[curr.description] ?? []), curr],
                        }),
                        {},
                    );
                    setData(mapped);
                })
                .finally(() => setLoading(false));
        }
    }, [searchParams]);

    const onFormFinish = (f) => {
        //setCollapseKey();
        f.startDate = f.interval?.interval?.[0].format("YYYY-MM-DD");
        f.endDate = f.interval?.interval?.[1].format("YYYY-MM-DD");
        f.period = f.interval.period;
        delete f.interval;
        [...Object.entries(f)].forEach(([k, _]) => searchParams.delete(k));
        [...Object.entries(f)].filter(([_, v]) => !!v).map(([k, v]) => searchParams.set(k, v));
        setSearchParams(searchParams, { replace: true });
    };
    return (
        <Spin spinning={loading}>
            <div style={{ display: "flex", flexDirection: "column", rowGap: 16 }}>
                <BackTitle backEnabled={false} padding={0} title={"Dashboard KPI"} />
                <Collapse
                    size="small"
                    activeKey={collapseKey}
                    onChange={setCollapseKey}
                    items={[
                        {
                            key: 1,
                            label: "Filtri",
                            children: (
                                <Form
                                    form={form}
                                    layout="vertical"
                                    onFinish={onFormFinish}
                                    initialValues={initialValues}
                                >
                                    <Space wrap>
                                        <Form.Item
                                            style={{ margin: 0 }}
                                            label="Intervallo"
                                            name="interval"
                                        >
                                            <PeriodRangePicker />
                                        </Form.Item>
                                        <Form.Item style={{ margin: 0 }} label="KPI" name="kpi">
                                            <Select
                                                filterOption={filterOption}
                                                options={kpis}
                                                style={{ width: 400 }}
                                                mode="multiple"
                                                maxTagCount={1}
                                                showSearch
                                                allowClear
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            style={{ margin: 0 }}
                                            label="Cliente"
                                            name="customerId"
                                        >
                                            <Select
                                                options={customers}
                                                style={{ width: 250 }}
                                                filterOption={filterOption}
                                                showSearch
                                                allowClear
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            style={{ margin: 0 }}
                                            label="Squadra"
                                            name="teamId"
                                        >
                                            <Select
                                                filterOption={filterOption}
                                                options={teams}
                                                style={{ width: 250 }}
                                                showSearch
                                                allowClear
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            style={{ margin: 0 }}
                                            label="Utente"
                                            name="userId"
                                        >
                                            <Select
                                                options={users}
                                                style={{ width: 250 }}
                                                showSearch
                                                allowClear
                                                filterOption={filterOption}
                                            />
                                        </Form.Item>
                                    </Space>

                                    <Divider orientation="left">Visualizzazione</Divider>
                                    <Form.Item
                                        style={{ margin: 0 }}
                                        label="Raggruppamento"
                                        name="group"
                                    >
                                        <Select
                                            filterOption={filterOption}
                                            options={selectOptions}
                                            showSearch
                                            allowClear
                                            style={{ width: 150 }}
                                        />
                                    </Form.Item>
                                    <Form.Item style={{ textAlign: "end", margin: 0 }}>
                                        <Space>
                                            <Button
                                                htmlType="submit"
                                                type="primary"
                                                style={{ width: 150 }}
                                            >
                                                Applica
                                            </Button>
                                            <Button
                                                style={{ width: 150 }}
                                                onClick={() => {
                                                    form.resetFields();
                                                    form.submit();
                                                }}
                                            >
                                                Reset
                                            </Button>
                                        </Space>
                                    </Form.Item>
                                </Form>
                            ),
                        },
                    ]}
                />
                <Row gutter={[16, 16]}>
                    {Object.entries(data)
                        .sort(([a, v1], [b, v2]) => b.localeCompare(a))
                        .map(([k, v]) => (
                            <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={8}>
                                <Card
                                    key={k}
                                    title={k}
                                    bodyStyle={{ height: 200, margin: 24 }}
                                >
                                    <TimeChart
                                        period={searchParams.get("period")}
                                        data={v.map((x) => ({ ...x, x: x.date, y: x.value }))}
                                        startDate={dayjs(searchParams.get("startDate"))}
                                        endDate={dayjs(searchParams.get("endDate"))}
                                    />
                                </Card>
                            </Col>
                        ))}
                </Row>
            </div>
        </Spin>
    );
}
